<script lang="ts" setup>
import { useProjectStore } from '@/store/projectStore';
import ProjectCustomProperties from './ProjectCustomProperties.vue';

interface Props {
  projectId: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const projectStore = useProjectStore();
const message = useAlerts();
const loading = ref(false);
const customProperties = ref<
  Record<string, string | number | boolean | string[] | null>
>({});
const hasValidationErrors = ref(false);

const project = computed(() =>
  projectStore.hasProject(props.projectId)
    ? projectStore.project(props.projectId)
    : null,
);

onMounted(async () => {
  await loadProject();
});

async function loadProject(): Promise<void> {
  loading.value = true;
  try {
    const project = await projectStore.loadProject(props.projectId);
    // Load custom properties if they exist
    if (project.custom_properties) {
      customProperties.value = { ...project.custom_properties };
    }
  } catch (error) {
    message.error(error);
  } finally {
    loading.value = false;
  }
}

function submit() {
  loading.value = true;

  projectStore
    .updateDetails(props.projectId, {
      custom_properties: customProperties.value,
    })
    .then(() => {
      message.success();
      projectStore
        .loadList()
        .then(() => {
          emit('close');
        })
        .catch((error) => {
          message.error(error);
          emit('close');
        });
    })
    .catch((error) => {
      message.error(error);
      emit('close');
    })
    .finally(() => {
      loading.value = false;
    });
}
</script>

<template>
  <UiModal
    :title="$t('customProperties.editProjectProperties')"
    @close="emit('close')"
  >
    <div v-if="loading && !customProperties" class="loading">
      <UiSpinner />
    </div>

    <div v-else class="custom-properties-content">
      <p class="subtitle">
        {{
          $t('customProperties.editProjectPropertiesSubtitle', {
            project: project?.title,
          })
        }}
      </p>

      <ProjectCustomProperties
        v-model="customProperties"
        v-model:has-validation-errors="hasValidationErrors"
        :project-id="props.projectId"
        :loading="loading"
      />

      <div class="actions">
        <UiButtonRow
          size="medium"
          :cancel-button-text="$t('button.cancel')"
          :disabled="loading || hasValidationErrors"
          :loading="loading"
          :action-button-text="$t('button.save')"
          has-space-between
          @cancel="emit('close')"
          @action="submit"
        />
      </div>
    </div>
  </UiModal>
</template>

<style lang="scss" scoped>
.loading {
  display: flex;
  justify-content: center;
  padding: var(--space-48);
}

.custom-properties-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);

  .subtitle {
    font-size: var(--font-size-14);
    color: var(--color-gray-500);
    font-weight: var(--font-weight-regular);
  }

  .actions {
    margin-top: var(--space-16);
    padding-top: var(--space-24);
    border-top: 1px solid var(--color-gray-200);
  }
}
</style>
